import { isNative } from 'src/utils/platform';
import { env } from 'src/config/env';
import axios from 'axios';
import { CapacitorHttp } from '@capacitor/core';
import { getCurrentUserIdToken } from 'src/modules/firebase';

const stackManagerClient = axios.create({ withCredentials: false });

let lastDiscoveryStatus: number | null = null;
export const getLastDiscoveryStatus = (): number | null => lastDiscoveryStatus;

const parseBody = (data: unknown): unknown =>
  typeof data === 'string' ? JSON.parse(data || '{}') : data;

// On native use CapacitorHttp: it bypasses the manager's CORS allowlist, which omits
// capacitor://localhost.
const discoveryGet = async <T>(url: string, token: string | undefined): Promise<T> => {
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  if (isNative()) {
    let res: Awaited<ReturnType<typeof CapacitorHttp.get>>;
    try {
      res = await CapacitorHttp.get({ url, headers });
    } catch (e) {
      lastDiscoveryStatus = 0;
      throw e;
    }
    lastDiscoveryStatus = res.status;
    if (res.status < 200 || res.status >= 300) {
      const err = new Error(`discovery ${res.status}`) as Error & { response?: { status: number } };
      err.response = { status: res.status };
      throw err;
    }
    return parseBody(res.data) as T;
  }
  try {
    const { data, status } = await stackManagerClient.get<T>(url, { headers });
    lastDiscoveryStatus = status;
    return data;
  } catch (e) {
    lastDiscoveryStatus = (e as { response?: { status?: number } })?.response?.status ?? 0;
    throw e;
  }
};

const base64UrlToBase64 = (segment: string): string => {
  const standard = segment.replace(/-/g, '+').replace(/_/g, '/');
  const paddingNeeded = (4 - (standard.length % 4)) % 4;
  return standard + '='.repeat(paddingNeeded);
};

export const decodeTokenAud = (token: string | undefined): string | null => {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(base64UrlToBase64(token.split('.')[1] ?? ''))) as { aud?: string };
    return payload.aud ?? null;
  } catch {
    return null;
  }
};

export type PreviewStackInfo = {
  stack_id: string;
  status: string;
  api_url: string | null;
  chat_url: string | null;
  ttl_expires: string;
  data_source: string;
};

export type StackSwitchPlan =
  | { kind: 'not-ready'; message: string }
  | { kind: 'keep-session' }
  | { kind: 'fresh-signup' };

export const planStackSwitch = (info: PreviewStackInfo): StackSwitchPlan => {
  if (info.status !== 'ready') {
    return { kind: 'not-ready', message: `Stack "${info.stack_id}" is ${info.status}, not ready yet` };
  }
  return info.data_source === 'clone:staging'
    ? { kind: 'keep-session' }
    : { kind: 'fresh-signup' };
};

export const fetchPreviewStack = async (slug: string): Promise<PreviewStackInfo> => {
  const base = env.stackManagerUrl;
  const token = await getCurrentUserIdToken();
  return discoveryGet<PreviewStackInfo>(`${base}/public/stacks/${encodeURIComponent(slug)}`, token);
};

export const listPreviewStacks = async (): Promise<PreviewStackInfo[]> => {
  const base = env.stackManagerUrl;
  const token = await getCurrentUserIdToken();
  const data = await discoveryGet<{ stacks: PreviewStackInfo[] }>(`${base}/public/stacks`, token);
  return data.stacks;
};

export const discoveryErrorMessage = (err: unknown): string => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 401) return 'Sign in on this origin to use preview stacks';
  if (status === 404) return 'No such stack';
  return 'Preview manager unreachable';
};
